import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { CommonService } from '../../common-service';
import { AuthService } from '../usuario/auth.service';
import { RessincronizarCertificadoEmpresaRequest } from '../../requests/integracao-financas-banco/ressincronizar-certificado-empresa.request';
import { RessincronizarCertificadoResponse } from '../../responses/integracao-financas-banco/ressincronizar-certificado.response';
import { DesvincularCertificadoResponse } from '../../responses/integracao-financas-banco/desvincular-certificado.response';
import { CertificadosStatusResponse } from '../../responses/integracao-financas-banco/certificados-status.response';

@Injectable({ providedIn: 'root' })
export class CertificadoIfbService extends CommonService {
    private apiUrl: string = environment.urlApiIntegracaoFinancasBanco + 'certificados';

    // Cache de 6h no localStorage para não refazer a requisição do status a cada navegação.
    private readonly CACHE_TTL_MS = 6 * 60 * 60 * 1000;
    private readonly CACHE_PREFIX = 'ats_certificados_status_';

    // Emite quando um certificado de banco é alterado, para o banner reconsultar e se atualizar/sumir.
    private readonly alteracoesSubject = new Subject<void>();
    public readonly alteracoes$ = this.alteracoesSubject.asObservable();

    constructor(
        private httpClient: HttpClient,
        private authService: AuthService,
    ) {
        super();
    }

    private get cacheKey(): string {
        const idEmpresa = this.authService.getUserInfos()?.idEmpresa ?? 0;
        return `${this.CACHE_PREFIX}${idEmpresa}`;
    }

    /**
     * Status dos certificados de boleto e PIX da empresa do token. Usa cache de 6h no localStorage;
     * passe forceRefresh=true para ignorar o cache.
     */
    public getStatus(forceRefresh: boolean = false): Observable<CertificadosStatusResponse> {
        if (!forceRefresh) {
            const cached = this.lerCache();
            if (cached) return of(cached);
        }

        return this.httpClient
            .get<CertificadosStatusResponse>(`${this.apiUrl}/status`, this.addHeaderToken())
            .pipe(
                tap(response => {
                    if (!response.isError) this.gravarCache(response);
                }),
                catchError(this.handleError.bind(this)),
            );
    }

    /**
     * Cascata: reaplica o eCNPJ recém-enviado em todos os bancos vinculados (usa_certificado_empresa).
     * Chamado direto pelo front (front→IFB, JWT) após trocar o eCNPJ e confirmar no modal.
     */
    public ressincronizarEmpresa(request: RessincronizarCertificadoEmpresaRequest): Observable<RessincronizarCertificadoResponse> {
        return this.httpClient
            .post<RessincronizarCertificadoResponse>(`${this.apiUrl}/ressincronizar-empresa`, request, this.addHeaderToken())
            .pipe(catchError(this.handleError.bind(this)));
    }

    /**
     * Desfaz o vínculo do eCNPJ da empresa com os bancos (usa_certificado_empresa = false).
     * Chamado pelo front após remover o certificado da empresa; os certificados já aplicados
     * nos bancos continuam valendo (operação local, sem chamada à integração).
     */
    public desvincularEmpresa(): Observable<DesvincularCertificadoResponse> {
        return this.httpClient
            .post<DesvincularCertificadoResponse>(`${this.apiUrl}/desvincular-empresa`, {}, this.addHeaderToken())
            .pipe(catchError(this.handleError.bind(this)));
    }

    /**
     * Notifica que um certificado de banco foi alterado (conexão/atualização de boleto ou PIX, cascata
     * ou desvínculo do e-CNPJ): invalida o cache da empresa e dispara para o banner reconsultar.
     */
    public notificarAlteracao(): void {
        this.invalidarCache();
        this.alteracoesSubject.next();
    }

    /** Invalida o cache da empresa logada (usar após alterar um certificado). */
    public invalidarCache(): void {
        try {
            localStorage.removeItem(this.cacheKey);
        } catch {
            /* localStorage indisponível - ignora */
        }
    }

    /** Remove todo o cache de status de certificado (qualquer empresa). Usar ao cair no login/logout. */
    public limparTodoCache(): void {
        try {
            const chaves: string[] = [];
            for (let i = 0; i < localStorage.length; i++) {
                const chave = localStorage.key(i);
                if (chave && chave.startsWith(this.CACHE_PREFIX)) chaves.push(chave);
            }
            chaves.forEach(chave => localStorage.removeItem(chave));
        } catch {
            /* localStorage indisponível - ignora */
        }
    }

    private lerCache(): CertificadosStatusResponse | null {
        try {
            const raw = localStorage.getItem(this.cacheKey);
            if (!raw) return null;
            const parsed = JSON.parse(raw) as { data: CertificadosStatusResponse; expiresAt: number };
            if (!parsed.expiresAt || Date.now() > parsed.expiresAt) {
                localStorage.removeItem(this.cacheKey);
                return null;
            }
            return parsed.data;
        } catch {
            return null;
        }
    }

    private gravarCache(data: CertificadosStatusResponse): void {
        try {
            const payload = { data, expiresAt: Date.now() + this.CACHE_TTL_MS };
            localStorage.setItem(this.cacheKey, JSON.stringify(payload));
        } catch {
            /* localStorage cheio/indisponível - segue sem cache */
        }
    }
}
